import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Loader2 } from "lucide-react";

import { useRightSidebar } from "../../contexts/RightSidebarContext";
import {
  updateQaLike,
  updateQaDislike,
  updateQaCatalog,
  getEnabledPublicModels,
  scrapePublicCitation,
} from "../../../api/chat";
import { getTeamMembers } from "../../../api/teamChat";

import {
  useChatThreads,
  useChatStream,
  useFileAttachments,
  useSourceSidebar,
  useTeamSocket,
  useTeamChat,
  useChatSocket,
} from "./hooks";

import { DEFAULT_AGENT } from "./utils/constants";
import { getUserUuid, isUserInArray } from "./utils/auth";

import { ChatEntry } from "./input";
import { ChatThread, renderPrivateThread, renderPublicThread } from "./thread";
import { renderTeamThread } from "./thread";
import CodeEditorSidebar from "./editor/CodeEditorSidebar";
import TypingIndicator from "./input/TypingIndicator";
import TeamReplyPanel from "./thread/TeamReplyPanel";
import TeamChatHeader from "./thread/TeamChatHeader";
import ArticleDetailPanel from "../../Universal/ArticleCardDetailPanel";
import { getTheme } from "../../Universal/useNavTheme";
import { getDocIdData } from "../../../api/universal";
import ShareInviteDetailPanel from "./messages/sharing/ShareInviteDetailPanel";
import CitationLoadingAnimation from "../../Universal/CitationLoadingAnimation";
import ThreadManager from "./thread/ThreadManager";

const SidebarPlaceholder = ({ state = "error", message }) => {
  if (state === "loading") {
    return (
      <div className="p-4 flex items-center gap-2 text-[13px] font-normal tracking-wide text-mutedText font-poppins">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span>{message}</span>
      </div>
    );
  }

  return (
    <div className="p-6 text-center text-[13px] font-normal tracking-wide text-mutedText font-poppins">
      {message}
    </div>
  );
};

// Bottom stack (typing indicator + input) shares the same column as the
// thread: capped at 4xl, left-aligned (no mx-auto), padded to match.
const BOTTOM_STACK_ALIGN = "w-full max-w-4xl px-6";

const MainChat = () => {
  const {
    isRightOpen,
    setIsRightOpen,
    setRightComponent,
    setRightSidebarMode,
  } = useRightSidebar();

  const [showThreadManager, setShowThreadManager] = useState(false);

  const [chatType, setChatType] = useState("private");
  const [selectedModel, setSelectedModel] = useState("");
  const [publicModels, setPublicModels] = useState([]);
  const [favoriteModels, setFavoriteModels] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("favoriteModels") || "[]");
    } catch {
      return [];
    }
  });

  const [historyDepth, setHistoryDepth] = useState(() => {
    try {
      const saved = parseInt(localStorage.getItem("historyDepth"), 10);
      return saved >= 0 && saved <= 20 ? saved : 5;
    } catch {
      return 5;
    }
  });

  const handleChangeDepth = useCallback((newDepth) => {
    setHistoryDepth(newDepth);
    localStorage.setItem("historyDepth", String(newDepth));
  }, []);

  const contextWindow = useMemo(() => {
    if (!selectedModel || publicModels.length === 0) return 128000;

    for (const group of publicModels) {
      for (const m of group.models || []) {
        const key = typeof m === "string" ? m : m.model_key;

        if (
          key === selectedModel &&
          typeof m === "object" &&
          m.max_input_tokens
        ) {
          return m.max_input_tokens;
        }
      }
    }

    return 128000;
  }, [selectedModel, publicModels]);

  const handleToggleFavorite = useCallback((modelKey) => {
    setFavoriteModels((prev) => {
      const next = prev.includes(modelKey)
        ? prev.filter((k) => k !== modelKey)
        : [...prev, modelKey];

      localStorage.setItem("favoriteModels", JSON.stringify(next));
      return next;
    });
  }, []);

  const [codeBlocks, setCodeBlocks] = useState([]);
  const [activeBlockId, setActiveBlockId] = useState(null);
  const codeBlockIdCounter = useRef(0);

  const handleOpenInEditor = useCallback(
    ({ language, code, label }) => {
      const id = `code-${++codeBlockIdCounter.current}`;

      setCodeBlocks((prev) => {
        const existing = prev.find(
          (b) => b.code === code && b.language === language,
        );

        if (existing) {
          setActiveBlockId(existing.id);
          return prev;
        }

        const next = [
          ...prev,
          {
            id,
            language,
            code,
            label: label || `${language || "code"} #${prev.length + 1}`,
          },
        ];

        setActiveBlockId(id);
        return next;
      });

      setRightSidebarMode("code-editor");
      setRightComponent(null);
      setIsRightOpen(true);
    },
    [setIsRightOpen, setRightComponent, setRightSidebarMode],
  );

  const handleCodeChange = useCallback((blockId, newCode) => {
    setCodeBlocks((prev) =>
      prev.map((b) => (b.id === blockId ? { ...b, code: newCode } : b)),
    );
  }, []);

  const handleCloseEditor = useCallback(() => {
    setIsRightOpen(false);
    setRightComponent(null);
    setRightSidebarMode(null);
  }, [setIsRightOpen, setRightComponent, setRightSidebarMode]);

  useEffect(() => {
    if (codeBlocks.length > 0 && activeBlockId) {
      setRightComponent(
        <CodeEditorSidebar
          codeBlocks={codeBlocks}
          activeBlockId={activeBlockId}
          onChangeBlock={setActiveBlockId}
          onCodeChange={handleCodeChange}
          onClose={handleCloseEditor}
          isStreaming={false}
        />,
      );
    }
  }, [
    codeBlocks,
    activeBlockId,
    handleCodeChange,
    handleCloseEditor,
    setRightComponent,
  ]);

  useEffect(() => {
    getEnabledPublicModels()
      .then((res) => {
        const data = res?.data?.data || [];
        setPublicModels(data);

        if (!selectedModel && data.length > 0 && data[0].models?.length > 0) {
          const first = data[0].models[0];
          setSelectedModel(typeof first === "string" ? first : first.model_key);
        }
      })
      .catch((e) => {
        console.error("Failed to load public models:", e);
        setPublicModels([]);
      });
  }, [selectedModel]);

  const teamSocket = useTeamSocket();

  const {
    threadPresence,
    threadTypingUsers,
    joinThread: joinChatThread,
    leaveThread: leaveChatThread,
    cancelStream: cancelChatStream,
    emitTyping: emitChatTyping,
    emitStopTyping: emitChatStopTyping,
  } = useChatSocket();

  const {
    channels: teamChannels,
    groups: teamGroups,
    dms: teamDms,
    currentTeamId,
    currentConversation,
    messages: teamMessages,
    hasMore: teamHasMore,
    loadingMessages: teamLoadingMessages,
    loadMore: teamLoadMore,
    selectConversation,
    openDM,
    openGroup,
    userUuid: teamUserUuid,
    loadConversations: reloadTeamConversations,
    lastReadTimestamp,
  } = useTeamChat({ teamSocket });

  const [teamMembers, setTeamMembers] = useState({});

  useEffect(() => {
    if (!currentTeamId) return;

    getTeamMembers(currentTeamId)
      .then((res) => {
        const members = res?.data?.members || [];
        const map = {};

        for (const m of members) {
          map[m.uuid] = m;
        }

        setTeamMembers(map);
      })
      .catch((e) => console.error("Failed to load members:", e));
  }, [currentTeamId]);

  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);

  const handleTeamInputChange = useCallback(
    (text) => {
      if (!currentTeamId) return;

      if (!isTypingRef.current) {
        teamSocket.emitTyping(currentTeamId);
        isTypingRef.current = true;
      }

      clearTimeout(typingTimeoutRef.current);

      typingTimeoutRef.current = setTimeout(() => {
        teamSocket.emitStopTyping(currentTeamId);
        isTypingRef.current = false;
      }, 2000);
    },
    [currentTeamId, teamSocket],
  );

  const handleTeamSend = useCallback(
    (message, mentionUuids = []) => {
      if (!currentTeamId || !message.trim()) return;

      teamSocket.sendMessage(currentTeamId, message.trim(), mentionUuids);

      clearTimeout(typingTimeoutRef.current);

      if (isTypingRef.current) {
        teamSocket.emitStopTyping(currentTeamId);
        isTypingRef.current = false;
      }
    },
    [currentTeamId, teamSocket],
  );

  const {
    allThreads,
    currentThreadId,
    setCurrentThreadId,
    localThreadIds,
    loadThreads,
    createNewThread,
    ensureThreadExists,
  } = useChatThreads({ threadType: chatType, selectedModel });

  const [selectedAgent, setSelectedAgent] = useState(DEFAULT_AGENT);
  const [thinkingMode, setThinkingMode] = useState("standard");

  useEffect(() => {
    console.log("[MainChat MOUNT]", Date.now());
    return () => console.log("[MainChat UNMOUNT]", Date.now());
  }, []);

  const {
    attachedFiles,
    fileInputRef,
    handleFilesSelected,
    removeAttachedFile,
    getReadyDocIds,
    clearAttachedFiles,
  } = useFileAttachments();

  const [speechMode, setSpeechMode] = useState(false);

  const {
    chatEntry,
    setChatEntry,
    messages,
    sentHistory,
    showLoading,
    streamingText,
    isStreaming,
    streamingCitations,
    statusText,
    isLoadingThread,
    sendPayload,
    handleStop,
    handleRetry,
    handleToggleLike: rawToggleLike,
    handleToggleDislike: rawToggleDislike,
    handleToggleCatalog: rawToggleCatalog,
    threadLocked,
    setThreadLocked,
    threadLockedByName,
    cancelDocumentId,
  } = useChatStream({
    currentThreadId,
    setCurrentThreadId,
    createNewThread,
    ensureThreadExists,
    loadThreads,
    localThreadIds,
    speechMode,
    getReadyDocIds,
    clearAttachedFiles,
    threadType: chatType,
    selectedModel,
    historyDepth,
  });

  const sendPayloadWithMode = useCallback(
    (md, agentApiName, threadId) => {
      const researchMode =
        agentApiName === "DISCOVERY" ? thinkingMode : undefined;

      return sendPayload(md, agentApiName, threadId, researchMode);
    },
    [sendPayload, thinkingMode],
  );

  const handleToggleLike = useCallback(
    (entry) => {
      rawToggleLike(entry);
      updateQaLike(!entry.like, entry.documentId);
    },
    [rawToggleLike],
  );

  const handleToggleDislike = useCallback(
    (entry) => {
      rawToggleDislike(entry);
      updateQaDislike(!entry.dislike, entry.documentId);
    },
    [rawToggleDislike],
  );

  const handleToggleCatalog = useCallback(
    (entry) => {
      rawToggleCatalog(entry);
      updateQaCatalog(entry.documentId);
    },
    [rawToggleCatalog],
  );

  const { citationInteractions, isRightOpen: rightOpen } =
    useSourceSidebar(messages);

  const [activePrivateCitationId, setActivePrivateCitationId] = useState(null);

  const handlePrivateSourceClick = useCallback(
    (citation) => {
      const documentId = citation?.doc_id;

      if (!documentId) {
        console.error("[MainChat] private citation missing doc_id:", citation);

        setRightSidebarMode("article-detail");
        setRightComponent(
          <SidebarPlaceholder message="Unable to load source: missing doc_id." />,
        );
        setIsRightOpen(true);
        return;
      }

      setActivePrivateCitationId(documentId);
      setRightSidebarMode("article-detail");
      setRightComponent(
        <SidebarPlaceholder state="loading" message="Loading article…" />,
      );
      setIsRightOpen(true);

      getDocIdData(documentId)
        .then((response) => {
          const article = response?.data;

          if (!article) {
            setRightComponent(
              <SidebarPlaceholder message="Unable to load article" />,
            );
            return;
          }

          const dark = document.documentElement.classList.contains("dark");

          setRightComponent(
            <ArticleDetailPanel
              key={documentId}
              article={article}
              cardId={documentId}
              t={getTheme(dark)}
            />,
          );
        })
        .catch((err) => {
          console.error("[MainChat] Failed to load private citation:", err);

          setRightComponent(
            <SidebarPlaceholder message="Unable to load article" />,
          );
        });
    },
    [setIsRightOpen, setRightComponent, setRightSidebarMode],
  );

  useEffect(() => {
    const handler = () => setShowThreadManager(true);
    window.addEventListener("chat:open-thread-manager", handler);

    return () =>
      window.removeEventListener("chat:open-thread-manager", handler);
  }, []);

  useEffect(() => {
    const pending = window.__pendingChatSelection;

    if (pending) {
      window.__pendingChatSelection = null;

      const { threadId, thread_type, team_id } = pending;
      setChatType(thread_type || "private");

      if (thread_type === "team" && team_id) {
        selectConversation(team_id);
      } else if (threadId !== undefined) {
        setCurrentThreadId(threadId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handler = (e) => {
      console.log("[receive sidebar:chat-thread-selected]", e.detail);

      const threadId = e.detail?.threadId ?? null;
      const threadType = e.detail?.thread_type || "private";
      const teamId = e.detail?.team_id ?? null;

      setShowThreadManager(false);
      setChatType(threadType);

      if (threadType === "team" && teamId) {
        selectConversation(teamId);
      } else {
        console.log("[setCurrentThreadId from event]", threadId);
        setCurrentThreadId(threadId);
      }
    };

    window.addEventListener("sidebar:chat-thread-selected", handler);

    return () =>
      window.removeEventListener("sidebar:chat-thread-selected", handler);
  }, [setCurrentThreadId, selectConversation]);

  const isFirstRunRef = useRef(true);

  useEffect(() => {
    if (isFirstRunRef.current) {
      isFirstRunRef.current = false;
      return;
    }

    window.dispatchEvent(
      new CustomEvent("sidebar:select-thread", {
        detail: { threadId: currentThreadId },
      }),
    );
  }, [currentThreadId]);

  useEffect(() => {
    if (chatType === "team") return;

    if (currentThreadId) {
      joinChatThread(currentThreadId);
    }

    return () => {
      if (currentThreadId) leaveChatThread(currentThreadId);
    };
  }, [currentThreadId, chatType, joinChatThread, leaveChatThread]);

  useEffect(() => {
    if (chatType !== "team" || !currentTeamId || teamMessages.length === 0) {
      return;
    }

    const unreadIds = teamMessages
      .filter(
        (m) =>
          m.sender_uuid !== teamUserUuid &&
          !(m.read_by || []).includes(teamUserUuid),
      )
      .map((m) => m.message_id);

    if (unreadIds.length > 0) {
      teamSocket.markRead(currentTeamId, unreadIds);
    }
  }, [chatType, currentTeamId, teamMessages, teamUserUuid, teamSocket]);

  const hasScrolledToUnread = useRef(false);

  useEffect(() => {
    if (chatType !== "team") return;
    if (teamMessages.length === 0) return;
    if (hasScrolledToUnread.current) return;

    setTimeout(() => {
      const el = document.getElementById("unread-separator");

      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        hasScrolledToUnread.current = true;
      }
    }, 150);
  }, [chatType, teamMessages]);

  useEffect(() => {
    hasScrolledToUnread.current = false;
  }, [currentTeamId]);

  const handleOpenSidebar = useCallback(() => {}, []);

  const [activeCitationUrl, setActiveCitationUrl] = useState(null);
  const [publicCitationInteractions, setPublicCitationInteractions] = useState(
    {},
  );

  useEffect(() => {
    if (!isRightOpen) {
      setActiveCitationUrl(null);
      setActivePrivateCitationId(null);
    }
  }, [isRightOpen]);

  useEffect(() => {
    setPublicCitationInteractions({});
    setActiveCitationUrl(null);
    setActivePrivateCitationId(null);
  }, [currentThreadId]);

  const userUuidRef = useRef(getUserUuid());

  useEffect(() => {
    if (chatType !== "public") return;

    const uid = userUuidRef.current;

    setPublicCitationInteractions((prev) => {
      const next = { ...prev };
      let changed = false;

      for (const msg of messages) {
        if (msg.type !== "answer" || !msg.citations) continue;

        for (const c of msg.citations) {
          const key = c.doc_id || c.url;

          if (!key || next[key]) continue;

          const likeArr = Array.isArray(c.like) ? c.like : [];
          const dislikeArr = Array.isArray(c.dislike) ? c.dislike : [];
          const catalogArr = Array.isArray(c.catalog) ? c.catalog : [];

          if (likeArr.length || dislikeArr.length || catalogArr.length) {
            next[key] = {
              like: isUserInArray(likeArr, uid),
              dislike: isUserInArray(dislikeArr, uid),
              catalog: isUserInArray(catalogArr, uid),
            };
            changed = true;
          }
        }
      }

      return changed ? next : prev;
    });
  }, [messages, chatType]);

  useEffect(() => {
    const handler = (e) => {
      const { url, interactions } = e.detail || {};

      if (url && interactions) {
        setPublicCitationInteractions((prev) => ({
          ...prev,
          [url]: interactions,
        }));
      }
    };

    window.addEventListener("public-citation:interact", handler);

    return () =>
      window.removeEventListener("public-citation:interact", handler);
  }, []);

  const handlePublicSourceClick = useCallback(
    (citation) => {
      const citationKey = citation.doc_id || citation.url;

      setActiveCitationUrl(citationKey);
      setRightSidebarMode("public-citation");

      setRightComponent(
        <div className="h-full">
          <CitationLoadingAnimation />
        </div>,
      );

      setIsRightOpen(true);

      scrapePublicCitation({ url: citation.url })
        .then((res) => {
          const data = res?.data;

          if (!data?.success && !data?.already_saved) {
            setRightComponent(
              <SidebarPlaceholder message="Unable to load citation" />,
            );
            return;
          }

          const dark = document.documentElement.classList.contains("dark");
          const t = getTheme(dark);
          const docId = data.doc_id;

          const article = {
            document_id: docId,
            title: data.title || data.domain || "Untitled",
            url: data.url || citation.url,
            source: data.source || data.domain || "",
            content: data.content || "",
            description: data.description || "",
            screenshot: data.screenshot || "",
            screenshot_full: data.screenshot_full || "",
            target_type: data.target_type || "web_citation",
            authors: data.authors || [],
            date_published: data.date_published || "",
            timestamp: data.date_published
              ? new Date(data.date_published).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })
              : "",
            like: !!data.like,
            dislike: !!data.dislike,
            catalog: !!data.catalog,
          };

          if (data.like || data.dislike || data.catalog) {
            setPublicCitationInteractions((prev) => ({
              ...prev,
              [citationKey]: {
                like: !!data.like,
                dislike: !!data.dislike,
                catalog: !!data.catalog,
              },
            }));
          }

          const onInteract = (_cardId, _docId, state) => {
            setPublicCitationInteractions((prev) => ({
              ...prev,
              [citationKey]: state,
            }));
          };

          setRightComponent(
            <CitationLoadingAnimation
              isComplete={true}
              statusMessage="Source captured"
              onCompleteFinished={() => {
                setRightComponent(
                  <ArticleDetailPanel
                    article={article}
                    cardId={docId}
                    t={t}
                    onInteract={onInteract}
                  />,
                );
              }}
            />,
          );
        })
        .catch((err) => {
          console.error("Failed to scrape citation:", err);

          setRightComponent(
            <SidebarPlaceholder message="Unable to load citation" />,
          );
        });
    },
    [setIsRightOpen, setRightComponent, setRightSidebarMode],
  );

  useEffect(() => {
    return () => {
      teamSocket?.setActiveConversation?.(null);
    };
  }, [teamSocket]);

  const currentPresence = Array.from(threadPresence.get(currentThreadId) || []);

  const currentTypingMap = threadTypingUsers.get(currentThreadId);
  const currentChatTyping = currentTypingMap
    ? Object.fromEntries(currentTypingMap)
    : {};

  const threadRenderOpts = {
    messages,
    onToggleLike: handleToggleLike,
    onToggleDislike: handleToggleDislike,
    onToggleCatalog: handleToggleCatalog,
    onRetry: handleRetry,
    isStreaming: isStreaming || showLoading,
    onOpenInEditor: handleOpenInEditor,
  };

  const [replyTo, setReplyTo] = useState(null);

  const renderedThread =
    chatType === "team"
      ? renderTeamThread({
          messages: teamMessages,
          userUuid: teamUserUuid,
          onlineUsers: teamSocket.onlineUsers,
          members: teamMembers,
          lastReadTimestamp: lastReadTimestamp,
          onCitationClick: (citation) => {
            handlePublicSourceClick(citation);
          },
          onEdit: (messageId, newContent) => {
            teamSocket.editMessage(currentTeamId, messageId, newContent);
          },
          onDelete: (messageId) => {
            teamSocket.deleteMessage(currentTeamId, messageId);
          },
          onReact: (messageId, emoji) => {
            teamSocket.reactToMessage(currentTeamId, messageId, emoji);
          },
          onPin: (messageId, pinned) => {
            teamSocket.pinMessage(currentTeamId, messageId, pinned);
          },
          onShareInviteClick: (msg) => {
            setRightSidebarMode("share-invite-detail");
            setRightComponent(
              <ShareInviteDetailPanel
                msg={msg}
                userUuid={teamUserUuid}
                onClose={() => {
                  setIsRightOpen(false);
                  setRightComponent(null);
                  setRightSidebarMode(null);
                }}
              />,
            );
            setIsRightOpen(true);
          },
          onArticleClick: (documentId) => {
            setRightSidebarMode("article-detail");
            setRightComponent(
              <SidebarPlaceholder state="loading" message="Loading article…" />,
            );
            setIsRightOpen(true);

            getDocIdData(documentId)
              .then((response) => {
                const article = response?.data;

                if (!article) return;

                const dark =
                  document.documentElement.classList.contains("dark");
                const t = getTheme(dark);

                const onInteract = (cardId, docId, state) => {
                  window.dispatchEvent(
                    new CustomEvent("shared-article:sidebar-interact", {
                      detail: { documentId: docId, ...state },
                    }),
                  );
                };

                const cardListener = (e) => {
                  const d = e.detail || {};

                  if (d.documentId !== documentId) return;

                  const updated = {
                    ...article,
                    like: d.like,
                    dislike: d.dislike,
                    catalog: d.catalog,
                  };

                  setRightComponent(
                    <ArticleDetailPanel
                      article={updated}
                      cardId={documentId}
                      t={getTheme(
                        document.documentElement.classList.contains("dark"),
                      )}
                      onInteract={onInteract}
                    />,
                  );
                };

                window.addEventListener(
                  "shared-article:card-interact",
                  cardListener,
                );

                setRightComponent(
                  <ArticleDetailPanel
                    article={article}
                    cardId={documentId}
                    t={t}
                    onInteract={onInteract}
                  />,
                );
              })
              .catch((err) => {
                console.error("Failed to load shared article:", err);

                setRightComponent(
                  <SidebarPlaceholder message="Unable to load article" />,
                );
              });
          },
          onReply: (messageId) => {
            const msg = teamMessages.find((m) => m.message_id === messageId);
            if (!msg) return;

            const existingReplies = teamMessages.filter(
              (m) => m.reply_to?.message_id === messageId,
            );

            setReplyTo(messageId);
            setRightSidebarMode("team-reply");
            setRightComponent(
              <TeamReplyPanel
                originalMessage={msg}
                initialReplies={existingReplies}
                members={teamMembers}
                userUuid={teamUserUuid}
                teamId={currentTeamId}
                onSend={(content, replyToId) => {
                  teamSocket.sendMessage(currentTeamId, content, [], replyToId);
                }}
                onClose={() => {
                  setReplyTo(null);
                  setIsRightOpen(false);
                  setRightComponent(null);
                  setRightSidebarMode(null);
                }}
              />,
            );
            setIsRightOpen(true);
          },
        })
      : chatType === "public"
        ? renderPublicThread({
            ...threadRenderOpts,
            onSourceClick: handlePublicSourceClick,
            isRightOpen: isRightOpen,
            activeSourceId: activeCitationUrl,
            citationInteractions: publicCitationInteractions,
          })
        : renderPrivateThread({
            ...threadRenderOpts,
            onOpenSidebar: handleOpenSidebar,
            onSourceClick: handlePrivateSourceClick,
            isRightOpen: rightOpen || isRightOpen,
            activeSourceId: activePrivateCitationId,
            citationInteractions,
          });

  const messageCount =
    chatType === "team" ? teamMessages.length : messages.length;

  const threadShowLoading =
    chatType === "team" ? teamLoadingMessages : isLoadingThread;

  const emptyStateMeta =
    chatType === "team" && currentConversation
      ? {
          type: currentConversation.type || "group",
          name:
            currentConversation.type === "dm"
              ? currentConversation.dm_display_name
              : currentConversation.name,
        }
      : null;

  return (
    <div className="h-full min-h-0 flex flex-col pt-14 font-poppins">
      {showThreadManager ? (
        <ThreadManager
          onClose={() => {
            setShowThreadManager(false);
            window.dispatchEvent(new CustomEvent("sidebar:refresh-threads"));
          }}
        />
      ) : (
        <div className="relative flex-1 min-h-0 flex flex-col">
          {chatType === "team" && currentTeamId && currentConversation && (
            <TeamChatHeader
              conversation={currentConversation}
              members={teamMembers}
              currentTeamId={currentTeamId}
              onLeave={() => {
                selectConversation(null);
                window.dispatchEvent(
                  new CustomEvent("sidebar:refresh-team-conversations"),
                );
              }}
            />
          )}

          <div className="flex-1 min-h-0 overflow-hidden">
            <ChatThread
              displayThisThread={renderedThread}
              showLoading={threadShowLoading}
              streamingText={chatType === "team" ? "" : streamingText}
              showStreamingOutput={chatType === "team" ? false : isStreaming}
              streamingCitations={chatType === "team" ? [] : streamingCitations}
              statusText={chatType === "team" ? "" : statusText}
              messageCount={messageCount}
              isTeamChat={chatType === "team"}
              threadKey={currentThreadId || currentTeamId || "empty"}
              emptyStateMeta={emptyStateMeta}
            />
          </div>

          <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none">
            {chatType === "team" && currentTeamId && (
              <div className={`pointer-events-auto ${BOTTOM_STACK_ALIGN}`}>
                <TypingIndicator
                  typingUsers={teamSocket.typingUsers[currentTeamId] || {}}
                  currentUserUuid={teamUserUuid}
                />
              </div>
            )}

            {chatType !== "team" &&
              !threadLocked &&
              Object.keys(currentChatTyping).length > 0 && (
                <div className={`pointer-events-auto ${BOTTOM_STACK_ALIGN}`}>
                  <TypingIndicator
                    typingUsers={currentChatTyping}
                    currentUserUuid={getUserUuid()}
                  />
                </div>
              )}

            <div className="pointer-events-auto">
              <ChatEntry
                chatEntry={chatEntry}
                setChatEntry={setChatEntry}
                selectedAgent={selectedAgent}
                setSelectedAgent={setSelectedAgent}
                thinkingMode={thinkingMode}
                setThinkingMode={setThinkingMode}
                sendPayload={sendPayloadWithMode}
                currentCase={currentThreadId}
                currentThreadId={currentThreadId}
                speechMode={speechMode}
                setSpeechMode={setSpeechMode}
                isStreaming={isStreaming || showLoading}
                onStop={handleStop}
                messages={chatType === "team" ? teamMessages : messages}
                sentHistory={sentHistory}
                attachedFiles={attachedFiles}
                onFilesSelected={handleFilesSelected}
                onRemoveFile={removeAttachedFile}
                fileInputRef={fileInputRef}
                chatType={chatType}
                publicModels={publicModels}
                selectedModel={selectedModel}
                onSelectModel={setSelectedModel}
                favoriteModels={favoriteModels}
                onToggleFavorite={handleToggleFavorite}
                contextWindow={contextWindow}
                historyDepth={historyDepth}
                onChangeDepth={handleChangeDepth}
                onTeamSend={handleTeamSend}
                onTeamInputChange={handleTeamInputChange}
                threadLocked={threadLocked}
                threadLockedByName={threadLockedByName}
                onUnlockThread={() => setThreadLocked(false)}
                onCancelStream={() => {
                  if (cancelDocumentId) {
                    cancelChatStream(currentThreadId, cancelDocumentId);
                  }
                }}
                onChatTyping={() => emitChatTyping(currentThreadId)}
                onChatStopTyping={() => emitChatStopTyping(currentThreadId)}
                currentTeamId={currentTeamId}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MainChat;
